import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { BadgeCheck, Check } from "lucide-react";
import { CopyIcon, XIcon } from "../../../components/icons";
import Toast from "../../Toast/Toast";
import { PRIMARY_BUTTON_CLASS } from "../utils/styles";

function TemplateIdRow({ id, label, isPrimary, onCopy, t }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;

    const timeoutId = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      onCopy?.();
    } catch {
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("tenant:failed_copy_clipboard"),
      });
    }
  };

  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${
        isPrimary
          ? "border-cfPrimaryBorder bg-cfPrimaryMuted/60"
          : "border-cfCardBorder bg-cfSecondary"
      }`}
    >
      <div className="min-w-0 flex-1">
        {label && (
          <p className="text-xs font-medium text-cfGlobalText mb-1">{label}</p>
        )}
        <p className="text-sm font-mono text-cfHeadingText break-all leading-relaxed">
          {id}
        </p>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg border border-cfCardBorder bg-cfSecondary text-cfGlobalText hover:text-cfPrimary hover:border-cfPrimaryBorder hover:bg-cfPrimaryMuted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
        aria-label={t("certificateOnboarding:onboard_modal_copy_id")}
      >
        {copied ? (
          <Check className="w-4 h-4 text-cfPrimary" aria-hidden />
        ) : (
          <CopyIcon size={16} className="inline-flex" aria-hidden />
        )}
      </button>
    </div>
  );
}

function CertificateOnboardSuccessModal({
  isOpen,
  onClose,
  onExit,
  onboardResult,
  certificateName,
  isTenantAdmin,
  t,
}) {
  const displayTemplateIds = useMemo(() => {
    if (!onboardResult) return [];
    const ids = onboardResult.template_ids || [];
    const primary = onboardResult.template_id;
    return [...new Set([primary, ...ids].filter(Boolean))];
  }, [onboardResult]);

  const primaryTemplateId = onboardResult?.template_id || displayTemplateIds[0] || "";
  const additionalIds = displayTemplateIds.filter((id) => id !== primaryTemplateId);

  const handleCopySuccess = () => {
    Toast({
      type: "success",
      title: t("certificateOnboarding:success"),
      message: t("tenant:copied_clipboard"),
    });
  };

  if (!onboardResult) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        id="cert-onboarding-success-modal"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-cfHeadingText/40 backdrop-blur-sm"
            aria-hidden
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-2xl border border-cfCardBorder bg-cfSecondary shadow-2xl transition-all">
                <div className="relative overflow-hidden bg-gradient-to-br from-cfPrimaryButton via-cfPrimary to-cfPrimaryLight px-6 pt-8 pb-10 text-center text-cfSecondary">
                  <div
                    className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10"
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute -left-6 bottom-0 h-24 w-24 rounded-full bg-white/10"
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    id="cert-onboarding-success-modal-close"
                    className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-cfSecondary hover:bg-white/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                    aria-label={t("certificateOnboarding:onboard_modal_close")}
                  >
                    <XIcon size={16} className="inline-flex" aria-hidden />
                  </button>

                  <div className="relative mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 ring-4 ring-white/25 shadow-lg">
                    <BadgeCheck className="h-8 w-8" strokeWidth={2} aria-hidden />
                  </div>
                  <Dialog.Title className="text-xl font-semibold tracking-tight">
                    {t("certificateOnboarding:onboard_modal_title")}
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-white/90 leading-relaxed max-w-sm mx-auto">
                    {certificateName
                      ? t("certificateOnboarding:onboard_modal_subtitle_named", {
                          name: certificateName,
                        })
                      : t("certificateOnboarding:onboard_modal_subtitle")}
                  </p>
                </div>

                <div className="px-6 py-6 space-y-5">
                  {primaryTemplateId && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-cfHeadingText">
                        {t("certificateOnboarding:complete_template_id_label")}
                      </p>
                      <TemplateIdRow
                        id={primaryTemplateId}
                        isPrimary
                        onCopy={handleCopySuccess}
                        t={t}
                      />
                    </div>
                  )}

                  {additionalIds.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-cfHeadingText">
                        {t("certificateOnboarding:complete_template_ids_label")}
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {additionalIds.map((id) => (
                          <TemplateIdRow
                            key={id}
                            id={id}
                            onCopy={handleCopySuccess}
                            t={t}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-cfGlobalText leading-relaxed">
                    {t("certificateOnboarding:onboard_modal_hint")}
                  </p>

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                    <button
                      type="button"
                      id="cert-onboarding-success-modal-dismiss"
                      onClick={onClose}
                      className="flex-1 px-4 py-2.5 border border-cfCardBorder text-cfHeadingText font-medium rounded-lg hover:bg-cfCardHover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
                    >
                      {t("certificateOnboarding:onboard_modal_close")}
                    </button>
                    <button
                      type="button"
                      id="cert-onboarding-success-modal-exit"
                      onClick={onExit}
                      className={`${PRIMARY_BUTTON_CLASS} flex-1 py-2.5 rounded-lg`}
                    >
                      {isTenantAdmin
                        ? t("certificateOnboarding:back_to_organisations")
                        : t("certificateOnboarding:back_to_tenants")}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default CertificateOnboardSuccessModal;
